import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SigntrustCertificate } from '@models/index.model';
import { SigntrustService, SigntrustUtilsService } from '@services/index.service';
import { TranslateService } from '@ngx-translate/core';
import { MatTableDataSource } from '@angular/material/table';
import { finalize, map, Subject, takeUntil } from 'rxjs';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DialogSignCertViewDetailsComponent } from './certificates-list/dialog-sign-cert-view-details/dialog-sign-cert-view-details.component';
import { environment } from '@environments/environment';

@Component({
  selector: 'dialog-select-certificate',
  templateUrl: './dialog-select-certificate.component.html',
  styleUrls: ['./dialog-select-certificate.component.scss'],
  standalone: false
})
export class DialogSelectCertificateComponent implements OnInit, OnDestroy {
  public isMobile: boolean;
  public sendingInfo: boolean = false;
  public currentLang!: string;
  public theme: string;
  public type!: string;
  public selectCertificate: SigntrustCertificate | undefined;

  private _destroy$: Subject<void> = new Subject();

  // List
  public dataSource: MatTableDataSource<any> = new MatTableDataSource();
  public focusedCertificate: SigntrustCertificate | undefined;
  public focusedCertificateForm!: UntypedFormGroup;
  public loadingInfo!: boolean;

  // Forms
  public certificateForms: Map<number, UntypedFormGroup> = new Map<number, UntypedFormGroup>();

  // List Filtering
  private _filterPredicate = (dataRow: SigntrustCertificate, filterValue: string) => {
    const parsedFilterValue = filterValue?.trim().toLowerCase();
    const parsedAlias = dataRow?.alias?.trim().toLowerCase();
    const parsedSubject = dataRow?.subject?.trim().toLowerCase();
    return parsedAlias?.includes(parsedFilterValue) || parsedSubject?.includes(parsedFilterValue);
  };

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _dialog: MatDialog,
    public dialogRef: MatDialogRef<DialogSelectCertificateComponent>,
    private _signtrustServ: SigntrustService,
    private _cdRef: ChangeDetectorRef,
    @Inject('environment') private _environment: any,
    private _translate: TranslateService,
    private _utilsSigntrust: SigntrustUtilsService,
    private _fb: UntypedFormBuilder,
  ) {
    this.isMobile = this._environment.platform === 'mobile';
    this.theme = this._environment.THEME;
  }

  public ngOnInit(): void {
    if (this.data) {
      console.log(this.data);
      this.type = this.data.type;
      this.selectCertificate = this.data.selectCertificate;
    }
    this.dataSource.filterPredicate = this._filterPredicate;
    this._getPreloadedCertificates();
  }

  public applyFilter(filterValue: string): void {
    this.dataSource.filter = filterValue;
    if (this.focusedCertificate && this.dataSource.filteredData.length > 1) {
      this.unfocusCertificate(this.focusedCertificate);
    }
    if (this.dataSource.filteredData.length === 1) {
      this.focusCertificate(this.dataSource.filteredData[0]);
    }
    this._cdRef.detectChanges();
  }

  private _getPreloadedCertificates(): void {
    this.loadingInfo = true;
    this._signtrustServ
      .getSigntrustCertificates()
      .pipe(
        takeUntil(this._destroy$),
        map(res => res.data),
        finalize(() => (this.loadingInfo = false))
      )
      .subscribe((certificateList: SigntrustCertificate[]) => {
        this.dataSource.data = this.sortCertificates(certificateList);
        certificateList.forEach((cert) => {
          this.certificateForms.set(cert.certificateHandler, this._createCertificateCheckPinForm(cert));
        });
        if (this.dataSource.filteredData.length === 1) {
          this.focusCertificate(this.dataSource.filteredData[0]);
        } else if (this.selectCertificate) {
          this.focusCertificate(this.selectCertificate);
        }
        this._cdRef.detectChanges();
      });
  }

  private sortCertificates(certificates: SigntrustCertificate[]): SigntrustCertificate[] {
    return certificates.sort((a, b) => {
      const aliasA = (a.alias || '').trim().toLowerCase();
      const aliasB = (b.alias || '').trim().toLowerCase();
      const isEmptyA = !aliasA;
      const isEmptyB = !aliasB;
      // Vacíos primero
      if (isEmptyA && !isEmptyB) return -1;
      if (!isEmptyA && isEmptyB) return 1;
      if (isEmptyA && isEmptyB) return 0;
      return aliasA.localeCompare(aliasB);
    });
  }

  // CERTIFICATE FORM
  private _createCertificateCheckPinForm(certificate: SigntrustCertificate): UntypedFormGroup {
    const formGroup: UntypedFormGroup = this._fb.group({
      certificateHandler: [certificate.certificateHandler],
      pin: [null],
      reason: [null],
    });

    if (certificate.needPin) {
      const pinControl: AbstractControl = formGroup.controls['pin'];
      pinControl.setValidators([Validators.required, Validators.maxLength(8), Validators.minLength(4)]);
      pinControl.updateValueAndValidity();
    }

    if (certificate.needReason) {
      const reasonControl: AbstractControl = formGroup.controls['reason'];
      const noWhiteSpaceRegex: RegExp = new RegExp(/[\S]/);
      reasonControl.setValidators([
        Validators.required,
        Validators.maxLength(1000),
        Validators.pattern(noWhiteSpaceRegex),
      ]);
      reasonControl.updateValueAndValidity();
    }
    return formGroup;
  }

  public focusCertificate(certificate: SigntrustCertificate) {
    if (certificate.certificateHandler !== this.focusedCertificate?.certificateHandler || this.dataSource.filteredData.length === 1) {
      if (this.focusedCertificate?.certificateHandler) {
        // Se ha seleccionado un nuevo certificado y ya había alguno seleccionado antes. Resetear el anterior.
        this.certificateForms.set(this.focusedCertificate.certificateHandler, this._createCertificateCheckPinForm(this.focusedCertificate));
      }
      // Se ha seleccionado un nuevo certificado. Emitir evento.
      this.focusedCertificate = certificate;
      this.onCertificateSelected(certificate);
    }
  }

  public unfocusCertificate(certificate: SigntrustCertificate) {
    if (certificate.certificateHandler === this.focusedCertificate?.certificateHandler) {
      this.focusedCertificate = undefined;
      this.onCertificateSelected(undefined);
    }
  }

  public openDetailsDialog(certificate: SigntrustCertificate) {
    this._dialog
      .open(DialogSignCertViewDetailsComponent, {
        width: '650px',
        disableClose: true,
        data: {
          certificate: certificate,
        },
      })
      .afterClosed()
      .subscribe();
  }

  public onCertificateSelected(selectCertificate: SigntrustCertificate | undefined) {
    this.selectCertificate = selectCertificate;
  }

  onSigning() {
    const result = {
      selectCertificate: this.selectCertificate,
      type: this.type
    }
    this.dialogRef.close(result);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
